import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { signOut } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { CFR_CATEGORIES } from "../../constants/cfrCategories";


const fStyle = { display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1rem' };
const lStyle = { fontSize: '0.8rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' };
const iStyle = { padding: '0.65rem 0.875rem', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '0.95rem', color: '#1a1a1a', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const taStyle = { ...iStyle, resize: 'vertical', minHeight: '80px' };

function Field({ label, value }) {
  return (
    <div style={fStyle}>
      <div style={lStyle}>{label}</div>
      <div style={{ fontSize: '0.95rem', color: '#1a1a1a' }}>{value || '--'}</div>
    </div>
  );
}

function EditInput({ label, value, onChange, type, maxLength }) {
  return (
    <div style={fStyle}>
      <label style={lStyle}>{label}</label>
      {type === 'textarea' ? (
        <textarea value={value} onChange={onChange} style={taStyle} />
      ) : (
        <input type={type || 'text'} value={value} onChange={onChange} style={iStyle} maxLength={maxLength} />
      )}
    </div>
  );
}

function OrgProfile() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [savedProfile, setSavedProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const profile = savedProfile || userProfile;

  const [shippingSame, setShippingSame] = useState(profile?.shippingSameAsMailing !== false);
  const [cfrCategories, setCfrCategories] = useState(profile?.cfrCategories || []);

  const [form, setForm] = useState({
    orgName: profile?.orgName || '',
    contactName: profile?.contactName || '',
    contactTitle: profile?.contactTitle || '',
    phone: profile?.phone || '',
    website: profile?.website || '',
    missionStatement: profile?.missionStatement || '',
    populationsServed: profile?.populationsServed || '',
    address: profile?.address || '',
    city: profile?.city || '',
    state: profile?.state || '',
    zip: profile?.zip || '',
    shippingAddress: profile?.shippingAddress || '',
    shippingCity: profile?.shippingCity || '',
    shippingState: profile?.shippingState || '',
    shippingZip: profile?.shippingZip || '',
    shippingContactName: profile?.shippingContactName || '',
    shippingInstructions: profile?.shippingInstructions || ''
  });

  function upd(field) {
    return e => setForm(prev => ({ ...prev, [field]: e.target.value }));
  }

  function toggleCfr(value) {
    setCfrCategories(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  }

  async function handleSave() {
    try {
      setError('');
      setSaving(true);
      await updateDoc(doc(db, 'organizations', auth.currentUser.uid), {
        orgName: form.orgName,
        contactName: form.contactName,
        contactTitle: form.contactTitle,
        phone: form.phone,
        website: form.website,
        missionStatement: form.missionStatement,
        populationsServed: form.populationsServed,
        cfrCategories,
        address: form.address,
        city: form.city,
        state: form.state,
        zip: form.zip,
        shippingSameAsMailing: shippingSame,
        shippingAddress: shippingSame ? form.address : form.shippingAddress,
        shippingCity: shippingSame ? form.city : form.shippingCity,
        shippingState: shippingSame ? form.state : form.shippingState,
        shippingZip: shippingSame ? form.zip : form.shippingZip,
        shippingContactName: form.shippingContactName || form.contactName,
        shippingInstructions: form.shippingInstructions || '',
        updatedAt: serverTimestamp()
      });
      setSavedProfile({ ...profile, ...form, cfrCategories, shippingSameAsMailing: shippingSame });
      setSuccess('Profile updated successfully.');
      setEditing(false);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSignOut() {
    await signOut(auth);
    navigate('/');
  }

  const s = {
    page: { minHeight: '100vh', background: '#f9fafb', fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif' },
    header: { background: 'white', borderBottom: '1px solid #eee', padding: '0 2rem', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 },
    logo: { fontSize: '1.2rem', fontWeight: 700, color: '#1e3a5f', cursor: 'pointer' },
    nav: { display: 'flex', gap: '2rem' },
    navLink: { cursor: 'pointer', fontSize: '0.9rem', color: '#555', padding: '0.5rem 0', borderBottom: '2px solid transparent' },
    navActive: { color: '#1e3a5f', fontWeight: 600, borderBottomColor: '#1e3a5f' },
    main: { maxWidth: '760px', margin: '0 auto', padding: '2.5rem 2rem' },
    card: { background: 'white', borderRadius: '16px', padding: '2rem', border: '1px solid #eee', marginBottom: '1.5rem' },
    sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#1e3a5f', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #eee' },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
    btnPrimary: { background: '#1e3a5f', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '0.95rem', cursor: 'pointer', fontWeight: 600 },
    btnSecondary: { background: 'transparent', color: '#1e3a5f', border: '1.5px solid #1e3a5f', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '0.95rem', cursor: 'pointer', fontWeight: 600 },
    btnSignout: { background: 'transparent', border: '1px solid #ddd', padding: '0.4rem 0.875rem', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer', color: '#555' },
    btnRow: { display: 'flex', gap: '1rem', marginTop: '1rem' },
    errBox: { background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' },
    okBox: { background: '#d1fae5', color: '#065f46', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' },
    checkRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem', color: '#555' }
  };

  const displayProfile = savedProfile || userProfile;

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.logo} onClick={() => navigate('/org')}>somebody.gives</div>
        <nav style={s.nav}>
          <span style={s.navLink} onClick={() => navigate('/org/dashboard')}>Dashboard</span>
          <span style={s.navLink} onClick={() => navigate('/org/needs')}>Manage needs</span>
          <span style={{ ...s.navLink, ...s.navActive }}>Profile</span>
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.9rem', color: '#333' }}>{displayProfile?.orgName}</span>
          <button onClick={handleSignOut} style={s.btnSignout}>Sign out</button>
        </div>
      </header>

      <main style={s.main}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: '0.25rem' }}>Organization profile</h1>
            <p style={{ color: '#555', fontSize: '0.95rem' }}>Keep your information current so donors and deliveries reach you correctly.</p>
          </div>
          {!editing && (
            <button style={s.btnPrimary} onClick={() => { setSuccess(''); setEditing(true); }}>Edit profile</button>
          )}
        </div>

        {error && <div style={s.errBox}>{error}</div>}
        {success && <div style={s.okBox}>{success}</div>}

        {/* Organization details */}
        <div style={s.card}>
          <div style={s.sectionTitle}>Organization details</div>
          {editing ? (
            <>
              <EditInput label="Organization name" value={form.orgName} onChange={upd('orgName')} />
              <EditInput label="Mission statement" value={form.missionStatement} onChange={upd('missionStatement')} type="textarea" />
              <EditInput label="Populations served" value={form.populationsServed} onChange={upd('populationsServed')} />
              <EditInput label="Website" value={form.website} onChange={upd('website')} />
            </>
          ) : (
            <>
              <Field label="Organization name" value={displayProfile?.orgName} />
              <Field label="Organization type" value={displayProfile?.orgType} />
              <Field label="EIN" value={displayProfile?.ein} />
              <Field label="Mission" value={displayProfile?.missionStatement} />
              <Field label="Populations served" value={displayProfile?.populationsServed} />
              <Field label="Website" value={displayProfile?.website} />
              <Field label="Status" value={displayProfile?.status} />
            </>
          )}
        </div>

        {/* Community Priority (CFR) */}
        <div style={s.card}>
          <div style={s.sectionTitle}>Community Priority (CFR)</div>
          <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem', lineHeight: 1.5 }}>
            Which of Springfield's Community Focus Report priority areas does your organization serve?
            These appear on your public profile and help donors find needs aligned with community priorities.
          </p>
          {editing ? (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {CFR_CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => toggleCfr(cat.value)}
                    style={{
                      padding: '0.35rem 0.875rem',
                      borderRadius: '20px',
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: cfrCategories.includes(cat.value) ? 'none' : '1px solid #ddd',
                      background: cfrCategories.includes(cat.value) ? '#1e3a5f' : 'white',
                      color: cfrCategories.includes(cat.value) ? 'white' : '#555',
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              {cfrCategories.length === 0 && (
                <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.5rem' }}>
                  No priorities selected.
                </p>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {(displayProfile?.cfrCategories || []).length === 0 ? (
                <span style={{ fontSize: '0.9rem', color: '#aaa' }}>None set — click Edit profile to add community priorities.</span>
              ) : (
                (displayProfile?.cfrCategories || []).map(val => {
                  const cat = CFR_CATEGORIES.find(c => c.value === val);
                  return cat ? (
                    <span key={val} style={{ padding: '0.3rem 0.875rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 600, background: '#e0f2fe', color: '#075985' }}>
                      {cat.label}
                    </span>
                  ) : null;
                })
              )}
            </div>
          )}
        </div>

        {/* Contact */}
        <div style={s.card}>
          <div style={s.sectionTitle}>Primary contact</div>
          {editing ? (
            <div style={s.row}>
              <EditInput label="Contact name" value={form.contactName} onChange={upd('contactName')} />
              <EditInput label="Title" value={form.contactTitle} onChange={upd('contactTitle')} />
              <EditInput label="Phone" value={form.phone} onChange={upd('phone')} type="tel" />
            </div>
          ) : (
            <div style={s.row}>
              <Field label="Contact name" value={displayProfile?.contactName} />
              <Field label="Title" value={displayProfile?.contactTitle} />
              <Field label="Email" value={displayProfile?.email} />
              <Field label="Phone" value={displayProfile?.phone} />
            </div>
          )}
        </div>

        {/* Mailing address */}
        <div style={s.card}>
          <div style={s.sectionTitle}>Mailing address</div>
          {editing ? (
            <>
              <EditInput label="Street address" value={form.address} onChange={upd('address')} />
              <div style={s.row}>
                <EditInput label="City" value={form.city} onChange={upd('city')} />
                <EditInput label="State" value={form.state} onChange={upd('state')} maxLength={2} />
                <EditInput label="ZIP" value={form.zip} onChange={upd('zip')} maxLength={5} />
              </div>
            </>
          ) : (
            <>
              <Field label="Address" value={displayProfile?.address} />
              <div style={s.row}>
                <Field label="City" value={displayProfile?.city} />
                <Field label="State" value={displayProfile?.state} />
                <Field label="ZIP" value={displayProfile?.zip} />
              </div>
            </>
          )}
        </div>

        {/* Shipping address */}
        <div style={s.card}>
          <div style={s.sectionTitle}>Shipping address</div>
          <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1rem' }}>Where donated supplies are delivered. Must be a physical street address.</p>
          {editing ? (
            <>
              <label style={s.checkRow}>
                <input type="checkbox" checked={shippingSame} onChange={e => setShippingSame(e.target.checked)} />
                Same as mailing address
              </label>
              {!shippingSame && (
                <>
                  <EditInput label="Shipping street address" value={form.shippingAddress} onChange={upd('shippingAddress')} />
                  <div style={s.row}>
                    <EditInput label="City" value={form.shippingCity} onChange={upd('shippingCity')} />
                    <EditInput label="State" value={form.shippingState} onChange={upd('shippingState')} maxLength={2} />
                    <EditInput label="ZIP" value={form.shippingZip} onChange={upd('shippingZip')} maxLength={5} />
                  </div>
                  <EditInput label="Receiving contact" value={form.shippingContactName} onChange={upd('shippingContactName')} />
                  <EditInput label="Delivery instructions" value={form.shippingInstructions} onChange={upd('shippingInstructions')} />
                </>
              )}
            </>
          ) : (
            <>
              {displayProfile?.shippingSameAsMailing ? (
                <Field label="Shipping address" value="Same as mailing address" />
              ) : (
                <>
                  <Field label="Address" value={displayProfile?.shippingAddress} />
                  <div style={s.row}>
                    <Field label="City" value={displayProfile?.shippingCity} />
                    <Field label="State" value={displayProfile?.shippingState} />
                    <Field label="ZIP" value={displayProfile?.shippingZip} />
                  </div>
                  <Field label="Receiving contact" value={displayProfile?.shippingContactName} />
                  <Field label="Delivery instructions" value={displayProfile?.shippingInstructions} />
                </>
              )}
            </>
          )}
        </div>

        {editing && (
          <div style={s.btnRow}>
            <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
            <button style={s.btnSecondary} onClick={() => { setEditing(false); setError(''); }}>
              Cancel
            </button>
          </div>
        )}

      </main>
    </div>
  );
}

export default OrgProfile;
